import React, { useState } from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import {
  Users,
  MailCheck,
  BookOpenText,
  MessageCircle,
  MessageCircleDashed,
  MessageSquareMore,
  Package,
  CreditCard,
  LogOut,
  Menu,
  X,
} from "lucide-react";

const AdminDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleLogout = () => {
    // Clear the admin token from local storage
    localStorage.removeItem("adminToken");
    // Redirect to the admin login page
    window.location.href = "/admin";
  };

  return (
    <div className="flex h-screen font-sans bg-gray-50 text-gray-900 overflow-hidden">
      {/* ──────────────────  Sidebar  ────────────────── */}
      <aside
        className={`bg-gray-950 border-r border-gray-900 shadow-[4px_0_24px_rgba(0,0,0,0.05)] fixed md:relative z-40 top-0 h-full w-[280px] p-6 transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        {/* Logo + Close (mobile) */}
        <div className="flex items-center justify-between mb-12 mt-4 md:mt-0">
          <div className="flex items-center gap-4">
            <img
              src="../../PakFashions-logo.jpg"
              alt="Pak Fashions logo"
              className="w-12 h-12 rounded-full border-2 border-yellow-500/30 object-cover shadow-lg shadow-yellow-500/20"
            />
            <h2 className="text-2xl font-black text-white tracking-tight">
              Pak <span className="text-yellow-500">Fashions</span>
            </h2>
          </div>

          {/* Close btn (mobile) */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-yellow-500 transition-colors p-2 rounded-full hover:bg-gray-900"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar pr-2">
          <ul className="space-y-1.5 text-gray-400 font-medium">
            {[
              { to: "visitors", icon: Users, label: "Visitor Details" },
              { to: "subscribed", icon: MailCheck, label: "Subscribed" },
              { to: "blog", icon: BookOpenText, label: "Blog" },
              { to: "testimonials", icon: MessageCircle, label: "Testimonials" },
              { to: "comments", icon: MessageSquareMore, label: "Comments" },
              { to: "faqs", icon: MessageCircleDashed, label: "FAQs" },
              { to: "products", icon: Package, label: "Manage Products" },
              { to: "payments", icon: CreditCard, label: "Payments" },
            ].map(({ to, icon: Icon, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-yellow-500/10 to-transparent text-yellow-500 font-bold border-l-4 border-yellow-500"
                        : "hover:bg-gray-900 hover:text-gray-100"
                    }`
                  }
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout */}
        <div className="pt-6 border-t border-gray-900 mt-auto">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-xl text-red-400 font-bold hover:bg-red-500/10 hover:text-red-300 transition-all duration-300"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </aside>

      {/* ──────────────────  Mobile Overlay ────────────────── */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-950/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* ──────────────────  Main Content  ────────────────── */}
      <main className="flex-1 bg-gray-50 flex flex-col h-full overflow-hidden relative">
        {/* Ambient Background Flares (More subtle & layered) */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-yellow-200/10 rounded-full blur-[140px] pointer-events-none transform translate-x-1/3 -translate-y-1/3 z-0"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-yellow-100/5 rounded-full blur-[100px] pointer-events-none transform -translate-x-1/4 translate-y-1/4 z-0"></div>
        
        {/* Top bar (mobile toggle & profile/header area) */}
        <header className="flex-shrink-0 bg-white/80 backdrop-blur-xl border-b border-gray-100 flex items-center justify-between px-6 md:px-10 py-5 z-20 sticky top-0 shadow-sm">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden text-gray-800 hover:text-yellow-600 transition-all p-2 rounded-full hover:bg-yellow-50 active:scale-95"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight hidden md:block">
              Admin <span className="text-gray-400 font-medium lowercase">/</span> Control Panel
            </h1>
            <h1 className="text-xl font-bold text-gray-900 md:hidden">
              Control Panel
            </h1>
          </div>
          
          {/* Top-Right Profile / Status Indicator */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end hidden sm:block">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">System</span>
              <span className="text-sm font-black text-gray-900">Live Status</span>
            </div>
            <div className="relative flex items-center justify-center">
              <div className="absolute w-3 h-3 bg-green-500 rounded-full animate-ping opacity-40"></div>
              <div className="relative w-3 h-3 bg-green-500 rounded-full shadow-[0_0_12px_rgb(34,197,94)] border border-white"></div>
            </div>
          </div>
        </header>

        {/* Outlet Canvas */}
        <div className="flex-1 overflow-y-auto p-6 md:p-10 relative z-10 custom-scrollbar scroll-smooth">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

/* Redirect /admin to /admin/products by default */
export const AdminRoutes = () => <Navigate to="products" replace />;

export default AdminDashboard;
